import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { getSubdivisions } from "~/app/queries/useSubdivisions"
import { useApolloClient } from "@apollo/client"
import { PaymentEngagementType } from "../../../../enums"
import * as Yup from "yup"

export const useSepaValidationSchema = (paymentFlow?: PaymentEngagementType) => {
  const apolloClient = useApolloClient()
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage")

  return useMemo(() => {
    return Yup.object().shape({
      name: Yup.string()
        .trim()
        .required(t("validation.required", { field: t("modal.accountHolderName") }))
        .min(3, t("validation.accountHolderName"))
        .max(100, t("validation.accountHolderName")),
      email: Yup.string()
        .email(t("validation.email"))
        .required(t("validation.required", { field: t("modal.email") })),
      iban: Yup.string().required(t("validation.required", { field: t("modal.iban") })),
      swiftBicCode: Yup.string().when([], {
        is: () => paymentFlow === PaymentEngagementType.Fte,
        then: Yup.string()
          .required(t("validation.required", { field: t("modal.swiftBicCode") }))
          .matches(
            /^([A-Za-z]){4}([A-Za-z]){2}([0-9A-Za-z]){2}([0-9A-Za-z]{3})?$/,
            t("validation.bicFormat"),
          ),
        otherwise: Yup.string(),
      }),
      addressLine1: Yup.string()
        .max(17, t("validation.maxLength", { length: 17 }))
        .trim()
        .required(t("validation.required", { field: t("modal.addressLine1") })),
      addressLine2: Yup.string()
        .max(17, t("validation.maxLength", { length: 17 }))
        .trim(),
      postalCode: Yup.string()
        .max(35, t("validation.maxLength", { length: 35 }))
        .trim()
        .required(t("validation.required", { field: t("modal.postalCode") })),
      city: Yup.string()
        .max(35, t("validation.maxLength", { length: 35 }))
        .trim()
        .required(t("validation.required", { field: t("modal.city") })),
      region: Yup.string().when(["countryCode"], (countryCode: string, schema) => {
        return schema.test(
          "Region required test",
          t("validation.required", { field: t("modal.region") }),
          async (subdivisionCode: string) => {
            const subdivisionsResponse = await getSubdivisions(apolloClient, countryCode || "")
            const isSubdivisionRequired = subdivisionsResponse?.isSubdivisionsArrayEmpty !== true
            return isSubdivisionRequired ? !!subdivisionCode : true
          },
        )
      }),
      countryCode: Yup.string()
        .trim()
        .required(t("validation.required", { field: t("modal.countryCode") })),
      isUserSigner: Yup.boolean(),
    })
  }, [apolloClient, paymentFlow, t])
}
